/**
 * IssuesPageHeader — Titel + Summary + Aktionen (Kanalregeln, Regeln anwenden).
 * P1/B5a: Regeln definieren + anwenden am selben Ort; Extraktion hält
 * IssuesPage unter dem 100-Zeilen-Component-Limit.
 */
import { useTranslation } from 'react-i18next'
import { RefreshCw } from 'lucide-react'

import { Button } from '@/components/v2/ui'
import { ChannelRulesEntry } from '@/components/v2/products/channel-rules'

export interface IssuesPageHeaderProps {
  productCount: number
  totalIssues: number
  recomputePending: boolean
  onRecompute: () => void
}

export function IssuesPageHeader({
  productCount,
  totalIssues,
  recomputePending,
  onRecompute,
}: IssuesPageHeaderProps) {
  const { t } = useTranslation()
  return (
    <header className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
          {t('productsIssues.title')}
        </h1>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          {t('productsIssues.workList.summary', {
            products: productCount,
            violations: totalIssues,
          })}{' '}
          (<span data-testid="issues-total">{totalIssues}</span>)
        </p>
      </div>
      <div className="flex items-center gap-2">
        <ChannelRulesEntry />
        <Button
          size="sm"
          variant="secondary"
          data-testid="issues-recompute-btn"
          onClick={onRecompute}
          disabled={recomputePending}
        >
          <RefreshCw className={`mr-1.5 h-4 w-4 ${recomputePending ? 'animate-spin' : ''}`} />
          {recomputePending
            ? t('productsIssues.recomputePending')
            : t('productsIssues.recompute')}
        </Button>
      </div>
    </header>
  )
}
